import { DownloadIcon } from "lucide-react"
import type * as React from "react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { useCan } from "@/features/auth/auth-context"
import { REPORT_EXPORT_FILES, downloadReportCsv } from "@/features/reports/use-reports"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Spinner } from "@/components/ui/spinner"
import { ApiError } from "@/lib/api-error"
import type { ReportPeriodPreset } from "@/lib/endpoints"

/**
 * The CA export (§17.4) — the one item on this screen with an external deadline.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * WHY THIS EXISTS AT ALL: without it somebody re-types a month of invoices into
 * the accountant's software by hand, which is the loop this whole app was built
 * to close. GSTR-1 for August is due around 11 September.
 *
 * FIVE FILES, NOT ONE BUTTON, AND ALL FIVE ARE OFFERED:
 *
 *   THE BILL REGISTER is first because it alone replaces the
 *     can-you-send-me-last-month's-bills conversation. It carries CANCELLED and
 *     DRAFT bills WITH their status — §17.4 in as many words — because the CA
 *     needs the gap in the number series explained, and it says on each row
 *     whether that row counts toward the totals.
 *   THE FOUR GSTR-1 TABLES follow. They are separate files because the tables
 *     have different columns and stacking them produces something no spreadsheet
 *     reads as one table — and because the offline filing tool takes them
 *     separately anyway.
 *   CDNR IS NOT OPTIONAL AND IS NOT HIDDEN BEHIND THE `credit_notes` FLAG. It is
 *     the one a shorter list would drop, and dropping it makes the owner
 *     over-report his liability (§17.3, §18.3 — a flag never changes
 *     correctness).
 *
 * THE FILES INHERIT THE PERIOD ABOVE, and the caption says which period that is.
 * The server resolves the window ONCE for the figures and the file alike, so a
 * CSV can never describe a different month from the screen it was downloaded
 * from. On 3 September he taps "Last month" and exports August.
 *
 * MONEY IN THE FILE IS PLAIN — `145600.00`, not `₹1,45,600.00` — and that is
 * deliberate, not an oversight: a machine reads those bytes, and a grouping
 * comma inside an unquoted CSV field is a column break. Indian digit grouping is
 * for the SCREEN and the printed document, and every figure above goes through
 * `formatRupees`. The caption below states this out loud, because the first
 * question anyone asks on opening the file is why the amounts look wrong.
 *
 * `report.export`, NOT `report.view` — a file leaves the building (§2.1). The
 * card is HIDDEN rather than disabled without it: a greyed-out export invites
 * hunting for the way to un-grey it, and the server refuses regardless.
 * ═════════════════════════════════════════════════════════════════════════════
 */
export function ReportExportCard({
  preset,
  from,
  to,
  /** True until the period has been resolved by the server — see the screen. */
  loading,
}: {
  preset: ReportPeriodPreset
  from: string
  to: string
  loading: boolean
}): React.JSX.Element | null {
  const { t } = useTranslation()
  const canExport = useCan("report.export")

  /** Which file is being fetched, so only its own button spins. */
  const [busyId, setBusyId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  if (!canExport) return null

  const download = async (fileId: string) => {
    const file = REPORT_EXPORT_FILES.find((candidate) => candidate.id === fileId)
    if (file === undefined) return

    setBusyId(file.id)
    setError(null)
    try {
      await downloadReportCsv(file, { preset, from, to })
    } catch (caught) {
      /*
       * The server's own sentence when it has one. An export failure is not a
       * transient hiccup to retry silently — a 403 means this account may not
       * take files out of the building, and a 400 names the query it refused.
       */
      setError(caught instanceof ApiError ? caught.message : t("err.somethingWentWrong"))
    } finally {
      setBusyId(null)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("reports.exportTitle")}</CardTitle>
        <CardDescription>{t("reports.exportHelp")}</CardDescription>
      </CardHeader>

      <CardContent className="flex flex-col gap-4">
        {error === null ? null : (
          <Alert variant="destructive">
            <AlertTitle>{t("reports.exportFailed")}</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <ul className="flex flex-col gap-3">
          {REPORT_EXPORT_FILES.map((file) => (
            <li key={file.id} className="flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">{t(file.labelKey)}</p>
                <p className="text-muted-foreground text-sm">{t(file.helpKey)}</p>
              </div>
              <Button
                variant="outline"
                /* §11.1 — 48px targets; the owner is 55 and on a phone. */
                className="min-h-11"
                disabled={loading || busyId !== null}
                onClick={() => {
                  void download(file.id)
                }}
              >
                {busyId === file.id ? (
                  <Spinner className="size-4" />
                ) : (
                  <DownloadIcon className="size-4" />
                )}
                {t("btn.exportForCa")}
              </Button>
            </li>
          ))}
        </ul>

        {/*
          * WHY THE AMOUNTS IN THE FILE LOOK DIFFERENT. Said here rather than
          * discovered by the CA: the file is read by software, so its numbers
          * are plain, and the rupee grouping he sees on this screen is a
          * display rule (CLAUDE.md), not a property of the figure.
          */}
        <p className="text-muted-foreground text-sm">{t("reports.exportFormatNote")}</p>
      </CardContent>
    </Card>
  )
}
